import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../services/api';
import { ownsEvents, isEventManager } from '../utils/roles';
import {
  FaCamera, FaUser, FaSignOutAlt, FaChartBar,
  FaImages, FaCog, FaDollarSign, FaHome,
  FaCalendarAlt, FaPlusCircle, FaBars, FaTimes, FaShoppingCart, FaCreditCard,
  FaUsers, FaWallet
} from 'react-icons/fa';
import './Navbar.css';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { items: cartItems, count: cartCount, total: cartTotal, removeItem } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // The mobile menu is a fixed full-screen panel stacked above the page, so
  // leaving it open after a navigation hides whatever the user just opened.
  // Closing on every route change covers programmatic navigation and the
  // browser's back/forward buttons, not just taps on the links themselves.
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    setCartOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    setCartOpen(false);
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    setCartOpen(false);
  };

  const toggleCart = () => {
    setCartOpen(!cartOpen);
    setUserMenuOpen(false);
  };

  return (
    <nav className="navbar">
      {/* Tapping the page behind the open panel dismisses it. Rendered before
          the menu so the panel paints on top of it. */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu-backdrop"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      <div className="navbar-container">
        {/* Logo */}
        <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
          <FaCamera className="logo-icon" />
          <span className="logo-text">PhotoMatch</span>
        </Link>

        {/* Mobile Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>

        {/* Main Menu */}
        <ul className={`navbar-menu ${mobileMenuOpen ? "mobile-active" : ""}`}>
          <li>
            <NavLink
              to="/pricing"
              className="navbar-link"
              onClick={closeMobileMenu}
            >
              <FaDollarSign />
              <span>Tarifs</span>
            </NavLink>
          </li>

          {isAuthenticated ? (
            <>
              <li>
                <NavLink
                  to="/dashboard"
                  className="navbar-link"
                  onClick={closeMobileMenu}
                >
                  <FaHome />
                  <span>Tableau de bord</span>
                </NavLink>
              </li>

              

            

              {ownsEvents(user) && (
                <>

                  <li>
                    <NavLink to="/photographer" className="navbar-link">
                      Mes événements
                    </NavLink>
                  </li>

                  
                  <li>
                    <NavLink
                      to="/create-event"
                      className="navbar-link"
                      onClick={closeMobileMenu}
                    >
                      <FaPlusCircle />
                      <span>Créer un événement</span>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/analytics"
                      className="navbar-link"
                      onClick={closeMobileMenu}
                    >
                      <FaChartBar />
                      <span>Analyses</span>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/wallet"
                      className="navbar-link"
                      onClick={closeMobileMenu}
                    >
                      <FaWallet />
                      <span>Portefeuille</span>
                    </NavLink>
                  </li>

                  {isEventManager(user) && (
                    <li>
                      <NavLink
                        to="/manager/photographers"
                        className="navbar-link"
                        onClick={closeMobileMenu}
                      >
                        <FaUsers />
                        <span>Mon équipe</span>
                      </NavLink>
                    </li>
                  )}

                  <li>
                    <NavLink
                      to="/my-credited-photos"
                      className="navbar-link"
                      onClick={closeMobileMenu}
                    >
                      <FaCamera />
                      <span>Mes prises de vue</span>
                    </NavLink>
                  </li>

                </>
              )}

              {user?.role === "user" && (
                <>
                <li>
                  <NavLink
                    to="/my-photos"
                    className="navbar-link"
                    onClick={closeMobileMenu}
                  >
                    <FaImages />
                    <span>Mes photos</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/events"
                    className="navbar-link"
                    onClick={closeMobileMenu}
                  >
                    <FaCalendarAlt />
                    <span>Événements</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/payment-history"
                    className="navbar-link"
                    onClick={closeMobileMenu}
                  >
                    <FaCreditCard />
                    <span>Payments</span>
                  </NavLink>
                </li>

                <li className="navbar-cart-menu">
                  <button
                    type="button"
                    className="cart-menu-trigger"
                    onClick={toggleCart}
                    aria-label={`Panier avec ${cartCount} article${cartCount !== 1 ? 's' : ''}`}
                  >
                    <span className="cart-icon-wrap">
                      <FaShoppingCart />
                      {cartCount > 0 && (
                        <span className="cart-count-badge">{cartCount}</span>
                      )}
                    </span>
                    <span className="cart-label">Panier</span>
                  </button>

                  {cartOpen && (
                    <div className="cart-dropdown">
                      <div className="cart-dropdown-header">
                        <strong>Panier</strong>
                        <span>{cartCount} article{cartCount !== 1 ? 's' : ''}</span>
                      </div>

                      {cartItems.length === 0 ? (
                        <p className="cart-empty">Aucune photo ajoutée pour le moment.</p>
                      ) : (
                        <>
                          <div className="cart-items">
                            {cartItems.map(item => (
                              <div key={item.id} className="cart-item">
                                <div className="cart-item-info">
                                  <span className="cart-item-name">{String(item.name).split('/').pop()}</span>
                                  <span className="cart-item-price">{Number(item.price || 0).toFixed(2)} F</span>
                                </div>
                                <button
                                  type="button"
                                  className="cart-item-remove"
                                  onClick={() => removeItem(item.id)}
                                  aria-label={`Retirer ${item.name} du panier`}
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="cart-total-row">
                            <span>Total</span>
                            <strong>{cartTotal.toFixed(2)} F</strong>
                          </div>
                          <button
                            type="button"
                            className="cart-checkout-button"
                            onClick={() => {
                              setCartOpen(false);
                              setMobileMenuOpen(false);
                              navigate('/checkout');
                            }}
                          >
                            <FaCreditCard /> Payer maintenant
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </li>

                </>

                
              )}

              

              {/* User Menu */}
              <li className="navbar-user-menu">
                <button className="user-menu-trigger" onClick={toggleUserMenu}>
                  <div className="user-avatar">
                    {user?.profile_photo ? (
                      <img src={getImageUrl(user.profile_photo)} alt={user.name} />
                    ) : (
                      <span>{user?.name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <span className="user-name">{user?.name}</span>
                </button>

                {userMenuOpen && (
                  <div className="user-dropdown">
                    <div className="dropdown-header">
                      <p className="dropdown-name">{user?.name}</p>
                      <p className="dropdown-email">{user?.email}</p>
                      <span className="dropdown-role">{user?.role}</span>
                    </div>

                    <div className="dropdown-divider"></div>

                    <Link
                      to="/profile"
                      className="dropdown-link"
                      onClick={() => {
                        closeMobileMenu();
                        setUserMenuOpen(false);
                      }}
                    >
                      <FaUser />
                      <span>Profil</span>
                    </Link>

                    <Link
                      to="/settings"
                      className="dropdown-link"
                      onClick={() => {
                        closeMobileMenu();
                        setUserMenuOpen(false);
                      }}
                    >
                      <FaCog />
                      <span>Paramètres</span>
                    </Link>

                    <div className="dropdown-divider"></div>

                    <button
                      onClick={handleLogout}
                      className="dropdown-link logout-link"
                    >
                      <FaSignOutAlt />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                )}
              </li>

              <li className="mobile-logout-item">
                <button
                  type="button"
                  className="mobile-logout-button"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt />
                  <span>Déconnexion</span>
                </button>
              </li>
            </>
          ) : (
            <>
              <li>
                <Link
                  to="/login"
                  className="navbar-link"
                  onClick={closeMobileMenu}
                >
                  Connexion
                </Link>
              </li>
              <li>
                <Link
                  to="/register"
                  className="navbar-button"
                  onClick={closeMobileMenu}
                >
                  Commencer
                </Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
